import { stringifyLosslessJson } from '@anchorbase/pillar-sdk';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { JsonEditor } from './JsonEditor';
import { sambaConfigApi } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { useUnsavedChangesRegistration } from '@/lib/hooks/useUnsavedChangesGuard';
import type {
    MerchantConfig,
    RoleAssignment,
    RoleAssignmentCustomConfig,
    RoleAssignmentPermissionsView,
} from '@anchorbase/pillar-sdk';
import {
    mergeConfigs,
    MerchantConfigSchema,
    RoleAssignmentCustomConfigSchema,
} from '@anchorbase/pillar-sdk';
import { createComponentLogger } from '@/lib/logging/frontend-logger';

interface ConfigViewerCardProps {
    /** List of role assignments to choose from */
    roleAssignments: ReadonlyArray<RoleAssignment | RoleAssignmentPermissionsView>;
    /** Currently active role assignment ID (pre-selected) for legacy detailed rows. */
    activeRoleAssignmentId?: number | null;
    /** Currently active role assignment UUID (pre-selected) for public rows. */
    activeRoleAssignmentUuid?: string | null;
    /** Whether the user can edit user_config */
    canEdit?: boolean;
    /** Whether this is viewing the current user's own configs (uses any_user endpoints) */
    isOwnConfig?: boolean;
    /** Title override */
    title?: string;
    /** Description override */
    description?: string;
}

type ConfigViewerQueryData = {
    user_config: RoleAssignmentCustomConfig;
    org_config: MerchantConfig;
    effective_config: MerchantConfig;
};

function createEmptyMerchantConfig(): MerchantConfig {
    return MerchantConfigSchema.parse({});
}

function createEmptyRoleAssignmentUserConfig(): RoleAssignmentCustomConfig {
    return RoleAssignmentCustomConfigSchema.parse({});
}

export function ConfigViewerCard({
    roleAssignments,
    activeRoleAssignmentId = null,
    activeRoleAssignmentUuid = null,
    canEdit = false,
    isOwnConfig = false,
    title = 'Configuration',
    description = 'View configuration for selected role assignment',
}: ConfigViewerCardProps) {
    const log = createComponentLogger('ConfigViewerCard');
    const queryClient = useQueryClient();

    // Filter to only organizational role assignments
    const organizationalRoleAssignments = roleAssignments.filter(
        (ra) =>
            ('organization_id' in ra && ra.organization_id !== null) ||
            (ra.organization_uuid !== null && ra.organization_uuid !== undefined)
    );

    // Selected role assignment UUID
    const [selectedRoleAssignmentUuid, setSelectedRoleAssignmentUuid] = useState<string | null>(
        (() => {
            if (activeRoleAssignmentUuid !== null) {
                return activeRoleAssignmentUuid;
            }
            if (activeRoleAssignmentId === null) {
                return null;
            }
            return (
                organizationalRoleAssignments.find(
                    (ra) =>
                        'role_assignment_id' in ra &&
                        ra.role_assignment_id === activeRoleAssignmentId
                )?.role_assignment_uuid ?? null
            );
        })()
    );

    // Editing state
    const [isEditing, setIsEditing] = useState(false);
    const [editedUserConfig, setEditedUserConfig] = useState<RoleAssignmentCustomConfig>(
        createEmptyRoleAssignmentUserConfig()
    );

    useUnsavedChangesRegistration(isEditing);

    // Update selection when active role assignment changes
    useEffect(() => {
        if (activeRoleAssignmentUuid !== null) {
            setSelectedRoleAssignmentUuid(activeRoleAssignmentUuid);
            return;
        }
        if (activeRoleAssignmentId !== null) {
            setSelectedRoleAssignmentUuid(
                organizationalRoleAssignments.find(
                    (ra) =>
                        'role_assignment_id' in ra &&
                        ra.role_assignment_id === activeRoleAssignmentId
                )?.role_assignment_uuid ?? null
            );
        } else {
            setSelectedRoleAssignmentUuid(null);
        }
    }, [activeRoleAssignmentId, activeRoleAssignmentUuid, organizationalRoleAssignments]);

    // Fetch config for selected role assignment
    const {
        data: configData,
        isLoading,
        error,
    } = useQuery<ConfigViewerQueryData>({
        queryKey: ['roleAssignmentConfig', selectedRoleAssignmentUuid, isOwnConfig],
        queryFn: async (): Promise<ConfigViewerQueryData> => {
            if (selectedRoleAssignmentUuid === null) {
                const emptyMerchantConfig: MerchantConfig = createEmptyMerchantConfig();
                return {
                    user_config: createEmptyRoleAssignmentUserConfig(),
                    org_config: emptyMerchantConfig,
                    effective_config: emptyMerchantConfig,
                };
            }
            const roleAssignment = organizationalRoleAssignments.find(
                (ra) => ra.role_assignment_uuid === selectedRoleAssignmentUuid
            );
            if (!roleAssignment?.organization_uuid) {
                const emptyMerchantConfig: MerchantConfig = createEmptyMerchantConfig();
                return {
                    user_config: createEmptyRoleAssignmentUserConfig(),
                    org_config: emptyMerchantConfig,
                    effective_config: emptyMerchantConfig,
                };
            }

            // Use different endpoints based on whether viewing own config or admin view
            if (isOwnConfig) {
                if (selectedRoleAssignmentUuid === null) {
                    throw new Error('Missing role_assignment_uuid for selected role assignment');
                }
                const [userConfigResponse, orgConfigResponse] = await Promise.all([
                    sambaConfigApi.getMyRoleAssignmentUserConfig(selectedRoleAssignmentUuid),
                    sambaConfigApi.getMyRoleAssignmentOrgConfig(selectedRoleAssignmentUuid),
                ]);

                const userConfig: RoleAssignmentCustomConfig =
                    RoleAssignmentCustomConfigSchema.parse(userConfigResponse ?? {});
                const orgConfig: MerchantConfig = MerchantConfigSchema.parse(
                    orgConfigResponse ?? {}
                );
                const effectiveConfig: MerchantConfig = mergeConfigs(orgConfig, userConfig);

                return {
                    user_config: userConfig,
                    org_config: orgConfig,
                    effective_config: effectiveConfig,
                };
            } else {
                if (selectedRoleAssignmentUuid === null) {
                    throw new Error('Missing role_assignment_uuid for selected role assignment');
                }
                if (!roleAssignment.organization_uuid) {
                    throw new Error('Missing organization_uuid for selected role assignment');
                }
                const [userConfigResponse, orgConfigResponse] = await Promise.all([
                    sambaConfigApi.getRoleAssignmentConfig(selectedRoleAssignmentUuid),
                    sambaConfigApi.getOrgConfig(roleAssignment.organization_uuid),
                ]);

                const userConfig: RoleAssignmentCustomConfig =
                    RoleAssignmentCustomConfigSchema.parse(userConfigResponse ?? {});
                const orgConfig: MerchantConfig = MerchantConfigSchema.parse(
                    orgConfigResponse ?? {}
                );
                const effectiveConfig: MerchantConfig = mergeConfigs(orgConfig, userConfig);

                return {
                    user_config: userConfig,
                    org_config: orgConfig,
                    effective_config: effectiveConfig,
                };
            }
        },
        enabled: selectedRoleAssignmentUuid !== null,
    });

    // Update user config mutation
    const updateConfigMutation = useMutation({
        mutationFn: async (config: RoleAssignmentCustomConfig) => {
            if (selectedRoleAssignmentUuid === null) {
                throw new Error('No role assignment selected');
            }
            return sambaConfigApi.updateRoleAssignmentConfig(
                selectedRoleAssignmentUuid,
                config,
                configData?.user_config ?? {}
            );
        },
        onSuccess: () => {
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: ['roleAssignmentConfig', selectedRoleAssignmentUuid],
                }),
                (err) => {
                    log.error('Error invalidating queries', err);
                }
            );
            setIsEditing(false);
        },
    });

    // Initialize edited config when entering edit mode
    const handleStartEdit = () => {
        setEditedUserConfig(configData?.user_config ?? createEmptyRoleAssignmentUserConfig());
        setIsEditing(true);
    };

    const handleSave = () => {
        updateConfigMutation.mutate(editedUserConfig);
    };

    const handleCancel = () => {
        setEditedUserConfig(createEmptyRoleAssignmentUserConfig());
        setIsEditing(false);
    };

    // Format role assignment for display
    const formatRoleAssignment = (ra: RoleAssignment | RoleAssignmentPermissionsView) => {
        const orgName =
            ra.organization_name ||
            ('organization_id' in ra && ra.organization_id !== null
                ? `Org #${ra.organization_id}`
                : 'Organization');
        return `${ra.role} @ ${orgName}`;
    };

    // If no organizational role assignments, show message
    if (organizationalRoleAssignments.length === 0) {
        return (
            <Card>
                <CardHeader>
                    <CardTitle>{title}</CardTitle>
                    <CardDescription>{description}</CardDescription>
                </CardHeader>
                <CardContent>
                    <p className="text-sm text-muted-foreground">
                        No organizational roles assigned. Configuration is only available for
                        organizational roles.
                    </p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>{title}</CardTitle>
                <CardDescription>{description}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
                {/* Role Assignment Selector */}
                <div className="space-y-2">
                    <label className="text-sm font-medium">Role Assignment</label>
                    <Select
                        value={selectedRoleAssignmentUuid ?? ''}
                        onValueChange={(value) => {
                            setSelectedRoleAssignmentUuid(value);
                        }}
                        disabled={isEditing}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select a role assignment" />
                        </SelectTrigger>
                        <SelectContent>
                            {organizationalRoleAssignments.map((ra) => {
                                if (ra.role_assignment_uuid === undefined) {
                                    throw new Error(
                                        'role_assignment_uuid is required for config viewer selection'
                                    );
                                }

                                return (
                                    <SelectItem
                                        key={ra.role_assignment_uuid}
                                        value={ra.role_assignment_uuid}
                                    >
                                        {formatRoleAssignment(ra)}
                                    </SelectItem>
                                );
                            })}
                        </SelectContent>
                    </Select>
                </div>

                {/* Loading State */}
                {isLoading && (
                    <p className="text-sm text-muted-foreground">Loading configuration...</p>
                )}

                {/* Error State */}
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>
                            Failed to load configuration: {error.message}
                        </AlertDescription>
                    </Alert>
                )}

                {/* Config Sections */}
                {configData && !isLoading && (
                    <div className="space-y-6">
                        {/* Organization Config (read-only) */}
                        <div className="space-y-2">
                            <h4 className="text-sm font-semibold">Organization Configuration</h4>
                            <p className="text-xs text-muted-foreground">
                                Settings defined at the organization level (read-only)
                            </p>
                            {Object.keys(configData.org_config).length > 0 ? (
                                <pre className="text-sm bg-gray-50 p-4 rounded-md overflow-x-auto border">
                                    {stringifyLosslessJson(configData.org_config, null, 2)}
                                </pre>
                            ) : (
                                <p className="text-sm text-muted-foreground italic">
                                    No organization configuration set
                                </p>
                            )}
                        </div>

                        {/* User Config (editable) */}
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="text-sm font-semibold">User Configuration</h4>
                                    <p className="text-xs text-muted-foreground">
                                        Your personal settings that override organization settings
                                    </p>
                                </div>
                                {canEdit && !isEditing && (
                                    <Button variant="outline" size="sm" onClick={handleStartEdit}>
                                        Edit
                                    </Button>
                                )}
                            </div>
                            {isEditing ? (
                                <div className="space-y-4">
                                    <JsonEditor
                                        value={editedUserConfig}
                                        onChange={(value: Record<string, unknown>) =>
                                            setEditedUserConfig(
                                                RoleAssignmentCustomConfigSchema.parse(value)
                                            )
                                        }
                                        placeholder='{"key": "value"}'
                                    />
                                    {updateConfigMutation.error && (
                                        <Alert variant="destructive">
                                            <AlertDescription>
                                                Failed to save: {updateConfigMutation.error.message}
                                            </AlertDescription>
                                        </Alert>
                                    )}
                                    <div className="flex gap-2">
                                        <Button
                                            onClick={handleSave}
                                            disabled={updateConfigMutation.isPending}
                                        >
                                            {updateConfigMutation.isPending ? 'Saving...' : 'Save'}
                                        </Button>
                                        <Button variant="outline" onClick={handleCancel}>
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            ) : Object.keys(configData.user_config).length > 0 ? (
                                <pre className="text-sm bg-gray-50 p-4 rounded-md overflow-x-auto border">
                                    {stringifyLosslessJson(configData.user_config, null, 2)}
                                </pre>
                            ) : (
                                <p className="text-sm text-muted-foreground italic">
                                    No user configuration set
                                </p>
                            )}
                        </div>

                        {/* Effective Config (read-only) */}
                        <div className="space-y-2">
                            <h4 className="text-sm font-semibold">Effective Configuration</h4>
                            <p className="text-xs text-muted-foreground">
                                Final computed configuration (user settings override organization
                                settings)
                            </p>
                            {Object.keys(configData.effective_config).length > 0 ? (
                                <pre className="text-sm bg-blue-50 p-4 rounded-md overflow-x-auto border border-blue-200">
                                    {stringifyLosslessJson(configData.effective_config, null, 2)}
                                </pre>
                            ) : (
                                <p className="text-sm text-muted-foreground italic">
                                    No configuration set
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
